import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Mail, MapPin } from 'lucide-react';
import { Item, Stagger } from './Reveal';
import { site } from '../data/site';

// Contact cards adapted from Watermelon UI block "contact-1"; form fields carried over from the original site.
const methods = [
  {
    icon: Mail,
    title: 'Email us',
    text: 'Questions, projects or partnerships — we reply to every message.',
    action: site.email,
    href: `mailto:${site.email}`,
  },
  {
    icon: MapPin,
    title: 'Visit our office',
    text: site.address,
    action: 'Get directions',
    href: site.mapUrl,
    external: true,
  },
  {
    icon: Building2,
    title: 'Company',
    text: `${site.brand} is a trading name of ${site.company}, registered in Cyprus.`,
  },
];

const referralOptions = ['Found on Google', 'Followed a link from another site', 'A friend told me', 'Other'];

const fieldClass =
  'w-full rounded-md border border-line bg-paper-2 px-4 py-3 text-[0.95rem] text-ink shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] outline-none transition placeholder:text-muted/70 focus:border-sage focus:ring-2 focus:ring-sage/25';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', source: referralOptions[0], message: '', agree: false });
  const [sent, setSent] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Website enquiry from ${form.name}`;
    const body = `${form.message}\n\n—\nName: ${form.name}\nEmail: ${form.email}\nHow did you find us: ${form.source}`;
    window.location.href = `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <section id="contact" className="border-t border-line bg-paper-2 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Stagger className="max-w-2xl">
          <Item as="p" className="eyebrow">Contact us</Item>
          <Item as="h2" className="mt-5 text-4xl font-normal leading-[1.05] tracking-[-0.045em] text-balance sm:text-5xl">
            Let&rsquo;s create something{' '}
            <span className="font-serif italic tracking-[-0.02em] text-sage-deep">extraordinary</span> together
          </Item>
          <Item as="p" className="mt-5 text-lg text-ink-soft">
            Start your journey with us today. Send a message and our team will get back to you shortly.
          </Item>
        </Stagger>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_1.15fr] lg:gap-12">
          <Stagger className="grid gap-5">
            {methods.map(({ icon: Icon, title, text, action, href, external }) => (
              <Item
                key={title}
                className="flex gap-5 rounded-[1.5rem] bg-paper p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,1),0_0_0_1px_rgba(0,0,0,0.07),0_2px_4px_0_rgba(0,0,0,0.05)]"
              >
                <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-paper-2 text-forest shadow-card">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold tracking-[-0.02em]">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink-soft">{text}</p>
                  {action && (
                    <a
                      href={href}
                      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="mt-3 inline-flex items-center gap-1.5 break-all text-sm font-medium text-ink underline decoration-sage/50 underline-offset-4 transition hover:decoration-ink"
                    >
                      {action}
                      <ArrowRight className="size-3.5 -rotate-45" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </Item>
            ))}
          </Stagger>

          <Stagger amount={0.15}>
            <Item
              as="form"
              onSubmit={handleSubmit}
              className="rounded-[1.5rem] bg-paper p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,1),0_0_0_1px_rgba(0,0,0,0.07),0_2px_4px_0_rgba(0,0,0,0.05)] sm:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">Name</span>
                  <input required value={form.name} onChange={update('name')} className={fieldClass} placeholder="Your name" autoComplete="name" />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">Email</span>
                  <input required type="email" value={form.email} onChange={update('email')} className={fieldClass} placeholder="you@company.com" autoComplete="email" />
                </label>
              </div>

              <label className="mt-5 block">
                <span className="mb-2 block text-sm font-medium">How did you find us?</span>
                <select value={form.source} onChange={update('source')} className={fieldClass}>
                  {referralOptions.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </label>

              <label className="mt-5 block">
                <span className="mb-2 block text-sm font-medium">Message</span>
                <textarea required rows={5} value={form.message} onChange={update('message')} className={`${fieldClass} resize-y`} placeholder="Tell us about your business and goals" />
              </label>

              <label className="mt-5 flex items-start gap-3 text-sm text-ink-soft">
                <input required type="checkbox" checked={form.agree} onChange={update('agree')} className="mt-0.5 size-4 rounded border-line accent-forest" />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="font-medium text-ink underline underline-offset-4">Terms of Use</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="font-medium text-ink underline underline-offset-4">Privacy Policy</Link>.
                </span>
              </label>

              <button type="submit" className="btn-primary group mt-7 w-full sm:w-auto">
                Send message
                <ArrowRight className="size-4 -rotate-45 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
              </button>
              {sent && (
                <p className="mt-4 text-sm text-sage-deep" role="status">
                  Your email app should open with the message ready to send. If it doesn&rsquo;t, write to us at{' '}
                  <a href={`mailto:${site.email}`} className="underline">{site.email}</a>.
                </p>
              )}
            </Item>
          </Stagger>
        </div>
      </div>
    </section>
  );
}
